import { TablesExternalAssetsDisplayGridRowData } from '@tableau/data-catalog-client-components';
import { changeTagsOnCatalogContent } from '../CatalogTagsHelper';
import { TablesGraphQLResourceQuery } from '../fetchers/TablesGraphQLResourceQuery';
import { Messages } from '../../codegen/Localize';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ILuidItemInfo } from '../../ts/models/resourceInfoParts';
import { DeleteTablesRequest } from '../../ts/ServerApi/requests/DeleteTablesRequest';
import { GetTableActionsRequest } from '../../ts/ServerApi/requests/GetTableActionsRequest';
import { ITableCertificationParams, SetTableCertificationRequest } from '../../ts/ServerApi/requests/SetTableCertificationRequest';
import { ITableDescriptionParams, SetTableDescriptionRequest } from '../../ts/ServerApi/requests/SetTableDescriptionRequest';
import { ITableContactParams, SetTablesContactRequest } from '../../ts/ServerApi/requests/SetTablesContactRequest';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { TableAllowedActions } from '../../ts/ServerApi/types/TableAllowedActions';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { IActionResource, IListResource } from '../../ts/resources/Resource';

class TablesResource implements IActionResource<TableAllowedActions>, IListResource<TablesExternalAssetsDisplayGridRowData> {

  private allQuery = new TablesGraphQLResourceQuery();

  public all(): TablesGraphQLResourceQuery {
    return this.allQuery;
  }

  public actions(id: string): Promise<TableAllowedActions> {
    if (FeatureFlags.dataCatalogPermissionEnabled) {
      return WebHttpClient.sendRequest(new GetTableActionsRequest(id));
    } else {
      return Promise.resolve({
        setDescription: false,
        setPermissions: false,
        updateRemoveDataQuality: false,
        sendEmail: false,
      });
    }
  }

  public deleteTable = async (id: string, name: string): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new DeleteTablesRequest({ ids: [id] }))
      .then(r => this.toast(r, Messages.removeExternalContentSuccessful({ ITEM_NAME: name }), Messages.removeFailed()));
  }

  public setTableDescription = async (args: ITableDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetTableDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessfulWithWait(), Messages.editDescriptionFailure()));
  }

  public setTableCertification = async (args: ITableCertificationParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetTableCertificationRequest(args))
      .then(r => {
        return this.toast(r, Messages.editCertificationSuccessful(), Messages.editCertificationFailure());
      });
  }

  public setContact = async (args: ITableContactParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetTablesContactRequest(args));
  }

  public async changeTags(params: {
    tables: ReadonlyArray<ILuidItemInfo>;
    tagsToAdd: ReadonlyArray<string>;
    tagsToRemove: ReadonlyArray<string>;
  }): Promise<void> {
    const { tagsToAdd, tagsToRemove, tables } = params;
    const luids = tables.map(t => t.luid);
    return changeTagsOnCatalogContent({ luids, contentType: 'table', tagsToAdd, tagsToRemove });
  }

  public async changeTagsOnColumns(params: {
    columnLuids: ReadonlyArray<string>;
    tagsToAdd: ReadonlyArray<string>;
    tagsToRemove: ReadonlyArray<string>;
  }): Promise<void> {
    const { tagsToAdd, tagsToRemove, columnLuids } = params;
    return changeTagsOnCatalogContent({ luids: columnLuids, contentType: 'column', tagsToAdd, tagsToRemove });
  }

  private toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }
}

export const Tables = new TablesResource();
export type Tables = typeof Tables;
